"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { ChevronDown, ArrowRight } from "lucide-react";

interface MenuItem {
  title: string;
  href: string;
  description?: string;
}

interface MegaMenuSection {
  title: string;
  items: MenuItem[];
}

interface MegaMenuProps {
  title: string;
  sections: MegaMenuSection[];
  className?: string;
  isPublicPage?: boolean;
  isActive?: boolean;
  isTransitioning?: boolean;
}

export function MegaMenu({ title, sections, className = "", isPublicPage = false }: MegaMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
    // Small delay for smooth animation
    setTimeout(() => setIsVisible(true), 10);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Modern Menu Trigger */}
      <button className={`relative flex items-center gap-2 transition-all duration-300 px-4 py-2 rounded-lg text-base font-bold group cursor-pointer ${
        isPublicPage 
          ? 'text-[color:var(--homepage-primary)] hover:text-[var(--homepage-accent-purple)]' 
          : 'text-[color:var(--homepage-navbar-text)] hover:text-[color:var(--homepage-navbar-hover)]'
      }`}>
        <span className="relative z-10">{title}</span>
        <ChevronDown 
          className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'rotate-180' : ''} ${
            isPublicPage 
              ? isOpen ? 'text-[var(--homepage-accent-purple)]' : 'text-[color:var(--homepage-primary)]'
              : isOpen ? 'text-[color:var(--homepage-navbar-hover)]' : 'text-[color:var(--homepage-navbar-text)]'
          }`} 
        />
      </button>

      {/* Ultra Modern Dropdown Menu - Dark theme */}
      {isOpen && (
        <div
          ref={menuRef}
          className={`absolute top-full left-0 mt-3 ${sections.length === 1 ? 'w-80' : 'w-[500px]'} bg-[#f1f5f9] border-4 border-[#f1f5f9] rounded-2xl shadow-[color:var(--homepage-shadow-lg)] z-50 transition-all duration-300 ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-2 scale-95'
          }`}
          style={{ 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(241, 245, 249, 0.5)'
          }}
        >
          {/* Up Arrow Indicator - Part of Container */}
          <div
            className={`absolute -top-[12px] left-12 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-[#f1f5f9] transition-opacity duration-300 ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
          ></div>
          <div className={`p-1 grid ${sections.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-1`}>
            {sections.map((section, sectionIndex) => (
              <div 
                key={sectionIndex}
                className="bg-white rounded-lg p-4 hover:shadow-md transition-all duration-300"
              >
                <h3 className="text-xs font-bold text-[color:var(--homepage-primary)] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <div className="w-1 h-1 bg-[color:var(--homepage-accent)] rounded-full"></div>
                  {section.title}
                  <ArrowRight className="w-3 h-3 text-[color:var(--homepage-primary)]" />
                </h3>
                <div className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <Link
                      key={itemIndex}
                      href={item.href}
                      className="group flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-all duration-300"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-[color:var(--homepage-primary)] text-sm transition-colors duration-300">
                          {item.title}
                        </span>
                        <ArrowRight className="w-4 h-4 text-[color:var(--homepage-primary)] opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1 flex-shrink-0" />
                        </div>
                        {item.description && (
                          <p className="text-xs text-gray-600 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
